/**
 * brandColor — Helpers de cor para a página pública de pagamento (/c/:slug).
 *
 * Toda string vinda do branding do tenant é tratada como input não confiável.
 * Antes de cair em uma CSS var precisa passar por `safeBrandColor`, evitando
 * injeção de tokens estranhos (ex.: `expression()` em browsers antigos ou
 * `url(...)` que vazaria contexto da página).
 *
 * RELEASE: 2026-10-13 v1.1.0 - Adicionado mixWithWhite: tom pastel por mistura em direção ao branco.
 * RELEASE: 2026-06-21 v1.0.0 — Versão inicial.
 */

const HEX_REGEX = /^#[0-9A-Fa-f]{6}$/;
const DEFAULT_BRAND = '#3DDC97';

/** Garante que a cor é um hex #RRGGBB válido — senão devolve o default. */
export function safeBrandColor(hex: string | null | undefined): string {
  if (!hex) return DEFAULT_BRAND;
  return HEX_REGEX.test(hex) ? hex : DEFAULT_BRAND;
}

/** Escurece (ou clareia, com valor negativo) um hex pelo percentual informado. */
export function shadeHex(hex: string, percent: number): string {
  const safe = safeBrandColor(hex).replace('#', '');
  const num = parseInt(safe, 16);
  const amt = Math.round(2.55 * percent);
  // eslint-disable-next-line no-bitwise
  const r = Math.max(Math.min((num >> 16) - amt, 255), 0);
  // eslint-disable-next-line no-bitwise
  const g = Math.max(Math.min(((num >> 8) & 0x00ff) - amt, 255), 0);
  // eslint-disable-next-line no-bitwise
  const b = Math.max(Math.min((num & 0x0000ff) - amt, 255), 0);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

/** Decide foreground (preto/branco) sobre uma cor de fundo via YIQ. */
export function pickReadableForeground(hex: string | null | undefined): string {
  const h = safeBrandColor(hex).replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 160 ? '#0B1320' : '#FFFFFF';
}

/**
 * Mistura a cor com branco na fração informada (0 a 1), produzindo tom pastel.
 *
 * Diferente de `shadeHex`, que soma um valor fixo a cada canal (mantém a cor
 * saturada e pode estourar canais em 255), aqui cada canal caminha proporcionalmente
 * até 255. O matiz é preservado e a saturação cai — que é o que faz a cor "ler"
 * como pastel. Frações altas deixam a cor mais suave; 0 devolve a cor original.
 */
export function mixWithWhite(hex: string | null | undefined, fraction: number): string {
  const safe = safeBrandColor(hex).replace('#', '');
  const num = parseInt(safe, 16);
  const f = Math.max(Math.min(fraction, 1), 0);
  const blend = (channel: number) => Math.round(channel + (255 - channel) * f);
  const r = blend((num >> 16) & 0x00ff);
  const g = blend((num >> 8) & 0x00ff);
  const b = blend(num & 0x00ff);
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}
